<template>
  <v-container v-if="state.document" class="document-viewer">
    <v-sheet
      max-width="600"
      class="mx-auto mb-5"
      v-if="state.document.status === 'complete'"
    >
      <v-alert type="success" elevation="2" class="mx-2" color="success" dark>
        <v-row no-gutters>
          <v-col cols="12">
            <p class="text-h6 text-white">Form Submitted</p>
            <p class="text-body-1 text-white">
              You have already submitted this form, you cannot edit it.
            </p>
          </v-col>
        </v-row>
      </v-alert>
    </v-sheet>
    <v-sheet class="mx-auto px-3 pb-12">
      <v-form
        validate-on="submit lazy"
        :disabled="state.document.status === 'complete'"
        class="pb-12"
      >
        <template
          v-for="(item, index) in state.document.data.items"
          :key="index"
        >
          <template v-if="item.type === 'section'">
            <form-field :item="item" @toggle-section="toggleSection">
              <template
                v-for="(subItem, subIndex) in getSectionItems(index)"
                :key="subIndex"
              >
                <TableView
                  v-if="
                    state.document.data.items[
                      subItem.originalIndex
                    ].content?.includes('<table')
                  "
                  v-model="
                    state.document.data.items[subItem.originalIndex].content
                  "
                />
                <span
                  v-else-if="
                    subItem.type === 'text' &&
                    !state.document.data.items[
                      subItem.originalIndex
                    ].content?.includes('<table')
                  "
                  v-html="processLinks(subItem.content)"
                  class="text-wrap"
                ></span>
                <form-field
                  v-else-if="
                    !state.document.data.items[
                      subItem.originalIndex
                    ].content?.includes('<table')
                  "
                  v-model="
                    state.document.data.items[subItem.originalIndex].value
                  "
                  :item="subItem"
                  :parent-section="item"
                />
              </template>
            </form-field>
          </template>
          <template v-else-if="!isItemInSection(index)">
            <TableView
              v-if="item.content && item.content.includes('<table')"
              v-model="item.content"
            />
            <span
              v-else-if="
                item.type === 'text' && item.content && !item.content.includes('<table')
              "
              v-html="processLinks(item.content)"
              class="text-wrap"
            ></span>
            <form-field
              v-else-if="item.type !== 'text' || (item.type === 'text' && !item.content)"
              v-model="state.document.data.items[index].value"
              :item="item"
              :data-index="index"
            />
          </template>
        </template>

        <div v-if="state.document.status !== 'complete'" class="fixed-buttons">
          <v-btn
            :loading="autoSaving"
            :color="settingsStore.settings.secondaryColor"
            block
            class="mt-2 text-white"
            text="Save"
            @click="submit(false)"
          >
          </v-btn>
          <v-btn
            :loading="
              settingsStore.loading && state.document.status === 'complete'
            "
            :color="settingsStore.settings.primaryColor"
            block
            class="mt-2 text-white"
            text="Save & Submit"
            @click="submit(true)"
          >
          </v-btn>
        </div>
      </v-form>
    </v-sheet>
  </v-container>
</template>

<script setup lang="ts">
import { reactive, computed, ref, nextTick, watch } from "vue";
import { documentService } from "@/api/services/document";
import { useSettingsStore } from "@/api/store/settings";
import { useRouter } from "vue-router";
import TableView from "./TableView.vue";

import FormField from "@/components/SwandoolaDocBuilder/FormField.vue";

const router = useRouter();

const settingsStore = useSettingsStore();

const state = reactive({
  document: null as any,
});

const autoSaving = ref(false);
let autoSaveTimer: ReturnType<typeof setTimeout> | null = null;

const documentId = computed(() => router.currentRoute.value.params.id);

const fetchDocument = async () => {
  documentService
    .show(documentId.value as string)
    .then((response) => {
      state.document = response;
    })
    .catch((error) => {
      console.log(error);
    });
};

fetchDocument();

const autoSave = async () => {
  if (!state.document || state.document.status === 'complete') return;
  autoSaving.value = true;
  try {
    await documentService.update(state.document.id, {
      data: state.document.data,
      completed: false,
    });
  } finally {
    autoSaving.value = false;
  }
};

watch(
  () => state.document?.data?.items,
  () => {
    if (!state.document || state.document.status === 'complete') return;
    if (autoSaveTimer) clearTimeout(autoSaveTimer);
    autoSaveTimer = setTimeout(autoSave, 1000);
  },
  { deep: true }
);

const checkRequiredFields = async (data: any): Promise<boolean> => {
  for (let index = 0; index < data.items.length; index++) {
    const item = data.items[index];

    if (
      item.isRequired &&
      (!item.value || item.value.toString().trim() === "")
    ) {
      await nextTick();

      const el = document.querySelector(`[data-index="${index}"]`) as HTMLElement;

      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.focus?.();
      }

      alert(`Please fill in the required field: "${item.label}"`);
      return false;
    }
  }

  return true;
};

const processLinks = (content: any) => {
  if (!content || content.length === 0) {
    return content;
  }

  // Regular expression to find URLs in text
  const urlRegex = /\bhttps?:\/\/[^\s<>]+/g;

  return content.replace(
    urlRegex,
    (url) => `<a href="${url}" target="_blank">${url}</a>`
  );
};

const submit = async (completed: boolean) => {
  if (completed && !(await checkRequiredFields(state.document.data))) {
    return;
  }

  const response = await documentService.update(state.document.id, {
    data: state.document.data,
    completed: completed,
  });

  fetchDocument();
};

const getSectionItems = (sectionIndex: number) => {
  const items = state.document.data.items;
  const nextSectionIndex = items.findIndex(
    (item, index) => index > sectionIndex && item.type === "section"
  );

  const endIndex = nextSectionIndex === -1 ? items.length : nextSectionIndex;

  return items
    .slice(sectionIndex + 1, endIndex)
    .map((item: any, index: number) => ({
      ...item,
      originalIndex: sectionIndex + 1 + index,
    }));
};

const isItemInSection = (index: number) => {
  const items = state.document.data.items;
  const previousItems = items.slice(0, index);
  const lastSection = previousItems.findLast((item) => item.type === "section");
  return !!lastSection;
};

const toggleSection = (sectionItem: any) => {
  sectionItem.isExpanded = !sectionItem.isExpanded;
};
</script>

<style scoped>
.text-wrap {
  word-wrap: break-word;
  overflow-wrap: break-word;
  white-space: normal;
  display: block;
}

:deep(ol) {
  padding-left: 14px;
}

:deep(p) {
  font-family: Helvetica, Arial, sans-serif;
  line-height: 1.625;
  font-weight: 300;
  margin-bottom: 1rem;
  color: black;
}

:deep(b, strong) {
  font-weight: bolder;
}

:deep(hr) {
  margin: 1rem 0;
  opacity: 0.5;
}

.fixed-buttons {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 16px 16px 20px 16px;
  max-width: 600px;
  margin: 0 auto;
  background: white;
}
</style>
